import { Box, Card, Chip, Divider, Stack, Typography } from '@mui/material';

import {
  APPROACH_META,
  REGISTRY,
  type DsApproach,
} from '@/designs-dev/inspect/ds-registry';
import EntryCard from '@/designs-dev/inspect/EntryCard';

const Group = ({
  title,
  blurb,
  names,
}: {
  title: string;
  blurb: string;
  names: string[];
}) => (
  <Stack spacing={2}>
    <Box>
      <Typography variant='h2'>{title}</Typography>
      <Typography variant='body1' color='text.secondary'>
        {blurb}
      </Typography>
    </Box>
    {names.map((n) => (
      <EntryCard key={n} name={n} entry={REGISTRY[n]} />
    ))}
  </Stack>
);

// Static hand-over doc: the whole registry, grouped by how a component should
// get its look. Same source of truth as the live inspector.
const HandoverSheet = () => {
  const byApproach = (a: DsApproach) =>
    Object.keys(REGISTRY).filter((n) => REGISTRY[n].approach === a);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'custom.neutral.100',
        p: 4,
        overflow: 'auto',
      }}
    >
      <Stack sx={{ mx: 'auto', maxWidth: 760 }} spacing={4}>
        <Box>
          <Typography variant='h1'>KareHero App - design system</Typography>
          <Typography variant='body1' color='text.secondary'>
            A component gets its look in one of three ways: pipe through MUI
            (the theme already styles it), a wrapped component (built once,
            reused), or a theme change (global, deliberate, never per-feature).
            sx is for layout, and for one-off visual styling that moves into the
            theme once it repeats. This is the same data the live inspector
            shows (toggle <code>&lt;/&gt;</code> under the phone).
          </Typography>
        </Box>

        <Group
          title='Pipe through MUI'
          blurb='Use the MUI component directly - the theme already styles it. Lay it out with sx; leave the look to the theme.'
          names={byApproach('pipe')}
        />
        <Divider />
        <Group
          title='Wrapped components'
          blurb='A reusable component owns the styling. Import and consume it; do not restyle at the call site.'
          names={byApproach('wrapped')}
        />
        <Divider />
        <Group
          title='Theme changes'
          blurb='A global token decision. It lands on every instance everywhere, so only make it when we are 100% sure.'
          names={byApproach('theme')}
        />
      </Stack>
    </Box>
  );
};

export default HandoverSheet;
